/**
 *  TopbarMobileMenuModal prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { AvatarLarge, LanguagesSwitcher, Button } from '..';
import config from '../../config';
import ProfileMenuContent from '../Topbar/components/ProfileMenuContent/ProfileMenuContent';
import AvailabilityBadge from '../AvailabilityBadge/AvailabilityBadge';
import ProfileMenuContentLoggedOut from '../Topbar/components/ProfileMenuContent/ProfileMenuContentLoggedOut';

import css from './TopbarMobileMenuModal.css';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { checkIfUserIsAnonymous, deanonymizeUser } from '../../util/anonym';

const { userTypeRider, userTypeHorseowner, anonymRiderName, anonymHorseownerName } = config;

const TopbarMobileMenuModal = ({
    isAuthenticated,
    currentPage,
    currentUser,
    currentUserUnreadMessagesRider,
    currentUserUnreadMessagesOwner,
    wishlistListingStatusChanged,
    hasDraftListing,
    ...rest
}) => {
    const history = useHistory();

    const [appIsMounted, setAppIsMounted] = useState(false);

    useEffect(() => {
        // Re-render for isomorphic purposes
        requestAnimationFrame(() => {
            setAppIsMounted(true);
        });
    }, []);

    const user = deanonymizeUser(ensureCurrentUser(currentUser));

    const {
        attributes: {
            profile: {
                firstName,
                publicData: { userType, availabilityStatus },
            },
        },
    } = user;

    const userIsRider = userType === userTypeRider;
    const userIsOwner = userType === userTypeHorseowner;
    const userIsAnonymous = checkIfUserIsAnonymous(user);
    const anonymName = userIsRider ? anonymRiderName : anonymHorseownerName;

    if (!isAuthenticated) {
        return (
            <>
                <div className={css.content}>
                    <div className={css.authenticationGreeting}>
                        <span>Hey!</span>
                    </div>
                </div>
                <ul className={css.profileMenuContentMob}>
                    <ProfileMenuContentLoggedOut currentPage={currentPage} isMobile />
                </ul>
                <LanguagesSwitcher />
            </>
        );
    }

    return !appIsMounted ? null : (
        <div className={css.root}>
            <section className={css.topBarMobTopSection}>
                <AvatarLarge className={css.avatar} user={currentUser} />

                <div className={css.topBarMenuAvailabilityHolder}>
                    {userIsRider && (
                        <AvailabilityBadge
                            user={currentUser}
                            rootClassName={classNames(
                                css.topBarAvailabilityBadge,
                                css[`availability-${availabilityStatus}`]
                            )}
                            editAllowed
                        />
                    )}
                    {userIsOwner && (
                        <Button
                            className={css.createNewListingLink}
                            type="button"
                            onClick={() =>
                                history.push(
                                    createResourceLocatorString(
                                        'NewListingPage',
                                        routeConfiguration()
                                    )
                                )
                            }
                        >
                            Pferd hinzufügen
                        </Button>
                    )}
                </div>
            </section>
            <h3 className={css.greeting}>
                <FormattedMessage
                    id="TopbarMobileMenuModal.greeting"
                    values={{ displayName: firstName }}
                />
            </h3>
            <ul className={classNames(css.profileMenuContentMob, css.loggedIn)}>
                <ProfileMenuContent
                    currentUser={user}
                    currentPage={currentPage}
                    wishlistListingStatusChanged={wishlistListingStatusChanged}
                    hasDraftListing={hasDraftListing}
                    ignoreMenuType
                    {...rest}
                />
            </ul>
            <LanguagesSwitcher />
        </div>
    );
};

const { bool, func, string } = PropTypes;

TopbarMobileMenuModal.propTypes = {
    isAuthenticated: bool.isRequired,
    currentUser: propTypes.currentUser,
    currentPage: string,
    onLogout: func.isRequired,
};

export default TopbarMobileMenuModal;
